import * as React from "react"

import { Button, ButtonGroup } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { PDF_VIEWS, type PdfView } from "../use-pdf-view-preference"

const VIEW_LABEL: Record<PdfView, string> = {
  label: "Label",
  master: "Master",
  side_by_side: "Side by side",
}

/** Which PDF the label page shows: the label with its boxes, its master, or both. */
export function PdfViewSwitch(props: { view: PdfView; onView: (view: PdfView) => void }) {
  return (
    <ButtonGroup label="Document" className="self-start">
      {PDF_VIEWS.map((v) => (
        <Button
          key={v}
          size="sm"
          variant={props.view === v ? "default" : "ghost"}
          aria-pressed={props.view === v}
          onClick={() => {
            props.onView(v)
          }}
        >
          {VIEW_LABEL[v]}
        </Button>
      ))}
    </ButtonGroup>
  )
}

export const NO_PAGE_MAP = "This run did not record which master page carries each label page."

/** Side by side: whether the master turns to the page carrying the label page shown. */
export function FollowSwitch(props: {
  checked: boolean
  onChange: (value: boolean) => void
  available: boolean
}) {
  const id = React.useId()
  const on = props.checked && props.available
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={on}
        aria-describedby={props.available ? undefined : `${id}-why`}
        disabled={!props.available}
        onClick={() => {
          props.onChange(!props.checked)
        }}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors",
          "outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
          "disabled:cursor-not-allowed disabled:opacity-50",
          on ? "border-brand bg-brand" : "border-border bg-surface-sunken",
        )}
      >
        <span
          aria-hidden
          className={cn(
            "inline-block size-3.5 rounded-full bg-surface shadow-e3 transition-transform",
            on ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </button>
      <label htmlFor={id} className="text-body-sm text-text-2">
        Follow label
      </label>
      {!props.available && (
        <span id={`${id}-why`} className="sr-only">
          {NO_PAGE_MAP}
        </span>
      )}
    </span>
  )
}
